import React from "react";
import {
    Backdrop,
    Box,
    Button,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    makeStyles,
    Paper,
    Typography,
} from "@material-ui/core";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import { LandingLayout } from "../index"; // A suitable money bag icon
import Turbocharge from "./Images/Turbocharge.png";
import { Formik, useFormikContext } from "formik";
import * as EstimationActions from "../../../actions/estimation";
import {
    CallingFromObject,
    ESTIMATE_MAX_AGE,
    genderDropDown,
    healthDropdown,
    IUL710PayEnum,
    IUL710TermEnum,
    primaryPlansOptions,
} from "../../../constants";
import * as Yup from "yup";
import {
    ageDropDown,
    isDebug,
    joinAsFullName,
    maskCurrency,
    noop,
    unmaskCurrency,
    va,
} from "../../../utils/commonUtils";
import { getQueryStringValue } from "../../../utils/queryString";
import { useSelector } from "react-redux";
import { RootState } from "../../../reducers";
import MyBenefitsPage from "../EstimateBenefits/MyBenefitsPage";
import { ErrorHelper, SelectField, TextField, TextFieldCommon } from "../../../components/formikFormInputs";
import { Label } from "../../../components/formInputs";
import { ANNUALLY, MONTHLY } from "../../client/Estimation/V1/Components/ContributionFrequency";
import { setOptions } from "../../client/Estimation/V1/Components/StepThreeTwo";
import { useActions } from "../../../actions";
import * as Actions from "../../../actions/verify";
import { useCreateAllocationPayLoad } from "../../client/Estimation/Hooks/Hooks";
import { useHistory } from "react-router-dom";
import * as EstimatorActions from "../../../actions/estimator";
import Socket from "../../client/Estimation/V1/Socket";
import { createEstimateReports } from "../../../components/client/Proposal";
import {
    submitEstimationFormik,
    SubmitEstimationFormikProps,
    submitIULEstimation,
} from "../../client/Estimation/V1/Components/EstimationFormikHandler";
import TestingProcessDialog from "../../../components/client/Estimation/TestingProcessDialog";
import FormikTokenUpdate from "../../client/Estimation/V1/Components/FormikTokenUpdate";
import * as clientActions from "../../../actions/client";


// --- Styles using makeStyles (MUI v4 standard) ---
const useStyles = makeStyles((theme) => ({
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: "#000",
    },
    root: {
        padding: theme.spacing(4),
        fontFamily: "sans-serif",
        color: "#333",
    },
    // --- Hero Section ---
    heroSection: {
        marginBottom: theme.spacing(8),
    },
    heroTitle: {
        fontWeight: 700,
        color: "#0D2F4B", // Dark blue
        marginBottom: theme.spacing(2),
    },
    leverageText: {
        color: "#007BFF", // Highlight blue
        display: "inline",
    },
    heroSubtitle: {
        color: "#555",
        marginBottom: theme.spacing(4),
        maxWidth: "500px",
    },
    heroButtons: {
        marginTop: theme.spacing(2),
        "& > *": {
            marginRight: theme.spacing(2),
        },
    },
    outlinedButton: {
        borderRadius: "20px",
        borderColor: "#007BFF",
        color: "#007BFF",
        textTransform: "none",
        fontWeight: "bold",
    },
    // --- Info Box (Right side of Hero) ---
    infoBox: {
        border: `3px solid #f9be11`,
        borderRadius: 6,
        padding: theme.spacing(3),
        backgroundColor: "#F9F9F9",
        height: "100%",
        position: "relative",
    },
    infoBoxTitle: {
        fontWeight: "bold",
        marginBottom: 0,
    },
    checkIcon: {
        color: "#28A745", // Green
        minWidth: "35px",
    },
    moneyIconContainer: {
        position: "absolute",
        bottom: theme.spacing(3),
        right: theme.spacing(3),
        color: "#28A745",
    },
    // --- Comparison Section ---
    comparisonSection: {
        border: `4px solid #f9be11`,
        borderRadius: 6,
        padding: theme.spacing(4),
    },
    sectionTitle: {
        marginBottom: theme.spacing(4),
    },
    calculatorCard: {
        padding: 10,
        boxShadow: "none",
        border: `1px solid #007BFF`,
        borderRadius: "10px",
        textAlign: "left",
    },
    formLabel: {
        textAlign: "left",
        color: "#6c757d",
        fontSize: "0.9rem",
        marginBottom: theme.spacing(0.5),
    },
    formControl: {
        marginBottom: theme.spacing(2),
    },
    calculateButton: {
        backgroundColor: "#007BFF",
        color: "white",
        borderRadius: "20px",
        padding: theme.spacing(1, 4),
        textTransform: "none",
        fontWeight: "bold",
        "&:hover": {
            backgroundColor: "#0056b3",
        },
    },
    benefitCard: {
        padding: theme.spacing(3),
        borderRadius: "10px",
        color: "white",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
    },
    blueCard: {
        backgroundColor: "#007BFF",
    },
    greenCard: {
        backgroundColor: "#28A745",
    },
    cardTitle: {
        fontWeight: "bold",
        marginBottom: theme.spacing(2),
    },
    cardMetricLabel: {
        opacity: 0.9,
        fontSize: "0.9rem",
    },
    cardMetricValue: {
        fontWeight: "bold",
        marginBottom: theme.spacing(2),
    },
    enrollButton: {
        backgroundColor: "white",
        borderRadius: "20px",
        textTransform: "none",
        fontWeight: "bold",
        padding: theme.spacing(1, 4),
    },
    moreLeverageCol: {
        border: "1px solid #007BFF",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-around",
        height: "100%",
        padding: 10,
        borderRadius: 6,
    },
    leverageDiff: {
        fontWeight: "bold",
        color: "#007BFF", // Green
    },
    // --- CTA Section ---
    ctaSection: {
        marginTop: theme.spacing(8),
        paddingTop: theme.spacing(4),
        borderTop: `1px solid #e0e0e0`,
        textAlign: "center",
    },
    ctaColumn: {
        padding: theme.spacing(2),
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
    },
    ctaTitle: {
        fontWeight: "bold",
        color: "#0D2F4B",
        marginBottom: theme.spacing(2),
        minHeight: "60px", // Align titles
    },
    ctaButton: {
        borderRadius: "20px",
        padding: theme.spacing(1, 4),
        textTransform: "none",
        fontWeight: "bold",
    },
    ctaEnroll: {
        backgroundColor: "#007BFF",
        color: "white",
        "&:hover": {
            backgroundColor: "#0056b3",
        },
    },
    ctaLearn: {
        borderColor: "#007BFF",
        color: "#007BFF",
    },
    ctaContact: {
        backgroundColor: "#6c757d",
        color: "white",
        "&:hover": {
            backgroundColor: "#5a6268",
        },
    },
}));


type IULEstimatorFormProps = {
    setIllustrationId: Function;
    getStepperCurrentState: Function;
    verifyInviteData?:any
}
const tobaccoDropdownOpn: object[] = [
    {id: "used", name: "Smoke",},
    {id: "notUsed", name: "Not Smoke",}
];
const contributionFrequencyOpn: object[] = [
    {id: ANNUALLY, name: ANNUALLY},
    {id: MONTHLY, name: MONTHLY},
];



const TurboChargeIul: React.FC<IULEstimatorFormProps> = (props) => {
    const kaizenCallingFrom: string = "ESTIMATOR";
    const iulCallingFrom: string = CallingFromObject.ENROLLMENT;
    const classes = useStyles();
    const history = useHistory();
    const userAuth = useSelector((state: RootState) => state.auth);
    const minCon = useSelector((state: RootState) => state.minimumContribution) || 21350;
    const verifyInviteData = useSelector((state: RootState) => state.verify);
    const inviteToken = getQueryStringValue("token");
    const user: any = !inviteToken && userAuth ? userAuth.user : verifyInviteData;
    const client = !inviteToken && userAuth ? user.client : verifyInviteData;
    const clientPolicy = !inviteToken && userAuth ? client && client.clientPolicies && client.clientPolicies.length > 0 && client.clientPolicies[0] : verifyInviteData.client;
    const kaizenStrategyId = 1; // kaizen strategy id
    const strategyId = clientPolicy && clientPolicy.strategy && clientPolicy.strategy.id;
    const carrierId = clientPolicy && clientPolicy.carrier && clientPolicy.carrier.id;
    const isNfIul = clientPolicy && clientPolicy.strategy && clientPolicy.strategy.slug === "iul";

    const [isReportDataLoading, setIsReportDataLoading] = React.useState<boolean>(false);
    const [estimatorData, setEstimatorData] = React.useState<any>(null);
    const [requestId, setRequestId] = React.useState<null | string>(null);
    const [iulRequestId, setIulRequestId] = React.useState<null | string>(null);
    const [isEstimationDataLoading, setIsEstimationDataLoading] = React.useState<boolean>(false);
    const [estimationResponse, setEstimationResponse] = React.useState<any>(null);
    const [issueAge, setIssueAge] = React.useState<number>((client && client.age) as number);
    const [iulEstimationResponse, setIulEstimationResponse] = React.useState<any>(null);
    const debugMode = isDebug();
    const createAllocationPayLoad = useCreateAllocationPayLoad((estimatorData && estimatorData.id),  (clientPolicy && clientPolicy.carrier && clientPolicy.carrier.id), clientPolicy && clientPolicy.clientId, user, isNfIul) ;
    const [token, setToken] = React.useState<any>(inviteToken);

    const estimationActions = useActions(EstimationActions);
    const estimatorActions = useActions(EstimatorActions);
    const cActions = useActions(clientActions);
    const verifyActions = useActions(Actions);

    const [isReportRequestCompleted, setIsReportRequestCompleted] = React.useState<boolean>(false);
    // const [runEstimation] = useRunEstimation(callingFrom, clientPolicy && clientPolicy.carrierId, strategyId, undefined, { ...user, clientPolicies: [clientPolicy] });

    const iulSubmission = async (values: any) => {
            // run new illustration
            const payload: SubmitEstimationFormikProps = {
                agentName: joinAsFullName(clientPolicy && clientPolicy.agent && clientPolicy.agent.user as any),
                atlantic: false,
                callingFrom: iulCallingFrom,
                clientPolicy,
                createAllocationPayLoad,
                estimationActions,
                estimatorData,
                issueAge: issueAge,
                loggedInUser: user,
                role: user.role,
                setIsEstimationDataLoading,
                setRequestId: setIulRequestId,
                user: user,
                userId: user.id,
                userName: joinAsFullName(user),
                values: {
                    ...values,
                    annualContribution: values.contributionAmount,
                    carrierId,
                    dob: client && client.dob,
                    InvestmentType: client && client.clientInvestmentType,
                },
            };
            debugMode && console.log(payload, payload.values);
            submitIULEstimation(payload).then();
        };
    const runKaizenEst = async (values) =>{
        // create user as per functions requirement
        const newUser = {
            ...user,
            accessType: "",
            client:  {
                clientPolicies: user.client ? [user.client] : [],
            },
            clientId: client && client.id,
            
        }

        // for kaizen estimation
        const props = {
            values: {
                healthType: values.healthType,
                tobacco: values.tobacco,
                primaryFocusPlan: "contributionAmount",
                beginDistributions: values.beginDistributions,
                endDistributions: values.endDistributions, 
                contributionFrequency: values.contributionFrequency, 
                gender: values.gender,
                totalCombinedContribution: values.contributionAmount
            },
            role: user && user.role,
            setIsEstimationDataLoading,
            userId: user && user.id,
            setRequestId,
            userName: joinAsFullName(user),
            agentName: joinAsFullName(user),
            issueAge: issueAge,
            estimatorData,
            estimationActions,
            callingFrom: kaizenCallingFrom,
            user: newUser,
            atlantic :false,
            clientPolicy,
            createAllocationPayLoad
        }
        submitEstimationFormik(
                props,
                undefined,
                undefined,
                undefined,
                true
        )
        
    }
    const initialValues = {
        /**
         * Default is the greater of the minimum value or 65.
         */
        beginDistributions: ESTIMATE_MAX_AGE,
        contributionAmount: "",
        contributionFrequency: "",
        /**
         * Default to 90. Minimum must be equal to minimum of Start Age.
         */
        endDistributions: 90,
        gender: client && client.gender,
        healthType: "",
        issueAge,
        primaryFocusPlan: "",
        tobacco: "",
    };
    const validationSchema = Yup.object().shape({
        beginDistributions: Yup.number().required("Start age is required"),
        contributionAmount: Yup.string()
                .required("Contribution Amount is required")
                .test("min-amount", `Min Amount should be ${maskCurrency(minCon + "")}`, (value) => {
                    if (!value) return false;
                    const num = unmaskCurrency(value); // remove commas if formatted
                    return !isNaN(num) && num >= minCon;
                }),
        contributionFrequency: Yup.string().required("Premium schedule is required"),
        endDistributions: Yup.number().required("End age is required").min(Yup.ref("beginDistributions"), "End age can't be less than start age"),
        gender: Yup.number().required("Sex is required"),
        healthType: Yup.string().required("Health status is required"),
        issueAge: Yup.number().required("Age is required").min(18).max(70),
        primaryFocusPlan: Yup.string().required("Contribution Years required"),
        tobacco: Yup.string().required("This field is required"),
    });

    const onCompleteReportDataLoading = (estimationResults: any) => {
        const responseData: any = estimationResults;
        // setIllustrationId && setIllustrationId(responseData && responseData.reports && responseData.reports.client && responseData.reports.client.illustrationId);
    };


    const socketButtonClick = () => {
        const payload: any = {
            callingFrom: kaizenCallingFrom,
            createAllocationPayLoad,
            estimationActions,
            initRequest: noop,
            onReportsAlreadyExist: () => setIsEstimationDataLoading(false),
            onRequestIdCreated: (id: any) => setRequestId(id),
            setIsEstimationDataLoading,
            setIsReportDataLoading,
            setIsReportRequestCompleted,
            setRequestId,
            runPublicly: true,
            user: {
                id: user.id,
                companyLinkUrl: clientPolicy && clientPolicy.company && clientPolicy.company.companyLinkUrl,
                loggerInUserId: user.id,
                clientPolicy,
                clientId: clientPolicy.clientId,
                role: user && user.role,
            },
        };
        createEstimateReports(payload).then();
    };

    React.useEffect(() =>{
        (async ()=>{
            if(clientPolicy && clientPolicy.id ){
            const clientPolicyId = clientPolicy.id
            const userId = user && user.id
            const estimationDetails: any = await estimationActions.getEstimationDetailsByUserId(userId, kaizenCallingFrom, clientPolicyId);
            const iulEstimationDetails: any = await estimationActions.getEstimationDetailsByUserId(userId, iulCallingFrom, clientPolicyId);
            
            if(estimationDetails){
                const stressResponse: any = JSON.parse(estimationDetails && estimationDetails.stressResponse);
                    if(stressResponse && stressResponse.success){
                        setEstimationResponse(stressResponse.success);
                    }
            }
            if (iulEstimationDetails) {
                const stressResponse: any = JSON.parse(iulEstimationDetails && iulEstimationDetails.stressResponse);
                if (stressResponse && stressResponse.success) {
                    setIulEstimationResponse(stressResponse.success);
                }
            }
        }
        })()
    },[clientPolicy && clientPolicy.id, user.id])

    React.useEffect(() => {
        (async () => {
            if (carrierId && strategyId) {
                // Get estimator details for strategyId 1 (Kaizen)
                const res = await estimatorActions.getEstimatorDetails({carrierId: carrierId, strategyId: kaizenStrategyId});
                if (!!res) {
                    setEstimatorData(res);
                }
            }
        })()
    }, [carrierId, strategyId])

    React.useEffect(() => {
        async function vfAccount() {
            await verifyActions.verifyAccount(inviteToken);
        }
        vfAccount();
    }, []);

    React.useEffect(() => {
        (async () => {
            const response: any = await estimationActions.getIssueAge(client && client.dob, clientPolicy && clientPolicy.carrierId);
            setIssueAge(response && response.issueAge);
        })();
    }, [client && client.dob, clientPolicy && clientPolicy.carrierId]);

    const onHandleSubmit = async (values: any) => {
        iulSubmission(values)
        runKaizenEst(values)
    }

    const PCClick = async (isIul:boolean = false) => {
        if(isIul){
            // update strategy here
            const isUpdated = await cActions.updateStrategyId({
                clientPolicyId: clientPolicy.id,
                // updating strategyId
                strategyId: 1,
            })
            if(isUpdated){
                window.scrollTo(0, 0);
                history.push(`/accept/invitation/${token}`);
            }
        }else{
            window.scrollTo(0, 0);
            history.push(`/accept/invitation/${token}`);
        }

    };

    const getMinContribution = async (query: any) => {
        await estimationActions.getMinimumContribution(query, isNfIul);
    }
    const KzClick = () => {
        window.scrollTo(0, 0);
        history.push(`/testersit.my.canva.site/kai-zen-microsite`);
    };


    const getDifference = () => {
        function getDifferences(responseData: any) {
            const illustration = responseData && responseData.illustration ? responseData.illustration : { totalincome: 0 };
            const illustrationTables = (illustration && illustration.data && illustration.data.table) || [];
            const table0PremiumPaid = illustrationTables && va(illustrationTables) && illustrationTables[0];
            const targetAgeCashSurrender = responseData && responseData.common && responseData.common.incomeagefrom - 1;
            const targetAgeDB = 95;
            const foundCashSurrender = illustrationTables.find((r: { age: number; }) => r.age === targetAgeCashSurrender);
            const foundDeathBenefit = illustrationTables.find((r: { age: number; }) => r.age === targetAgeDB);
            return [
                table0PremiumPaid ? table0PremiumPaid.premiumpaid : 0,
                foundCashSurrender ? foundCashSurrender.cashsurrender : 0,
                illustration.totalincome,
                foundDeathBenefit ? foundDeathBenefit.deathbenefit : 0,
            ]
        }
        const iulValues = getDifferences(iulEstimationResponse);
        const kzValues = getDifferences(estimationResponse);

        const benefitData = [
            { diff: `${maskCurrency((kzValues[0] - iulValues[0]).toString())}` },
            { diff: `${maskCurrency((kzValues[1] - iulValues[1]).toString())}` },
            { diff: `${maskCurrency((kzValues[2] - iulValues[2]).toString())}` },
            { diff: `${maskCurrency((kzValues[3] - iulValues[3]).toString())}` },
        ];

        return benefitData;
    }

    return (
            <LandingLayout>
                <Socket
                        callingFrom={kaizenCallingFrom}
                        closeAfterFullProcess={false}
                        handleButtonClick={socketButtonClick}
                        handleReset={noop}
                        handleResult={noop}
                        isReportRequestAutomated={false}
                        onCompleteReportDataLoading={onCompleteReportDataLoading}
                        page={"enrollment"}
                        requestId={requestId}
                        setIsEstimationDataLoading={setIsEstimationDataLoading}
                        setIsReportDataLoading={setIsReportDataLoading}
                        setIsReportRequestCompleted={setIsReportRequestCompleted}
                        setLifeDeathBenefitSeeking={noop}
                        setResponseData={setEstimationResponse}
                        userId={user && user.id}
                />
                <Socket
                    callingFrom={iulCallingFrom}
                    closeAfterFullProcess={false}
                    handleButtonClick={noop}
                    handleReset={noop}
                    handleResult={noop}
                    isReportRequestAutomated={false}
                    onCompleteReportDataLoading={noop}
                    page={"estimator"}
                    requestId={iulRequestId}
                    setIsEstimationDataLoading={noop}
                    setIsReportDataLoading={noop}
                    setIsReportRequestCompleted={noop}
                    setLifeDeathBenefitSeeking={noop}
                    setResponseData={setIulEstimationResponse}
                    userId={user && user.id}
                />

            {
                isEstimationDataLoading &&
                <Backdrop className={classes.backdrop} open={isEstimationDataLoading}>
                    <TestingProcessDialog
                        getEstimationProgress={estimationActions.getEstimationProgress}
                        handleClose={() => setIsEstimationDataLoading(false)}
                        isEstimationDataLoading={isEstimationDataLoading}
                        open={isEstimationDataLoading}
                    />
                </Backdrop>
            }
                <div className={classes.root}>
                    {/* ======================= Hero Section ======================= */}
                    <Grid container spacing={4} className={classes.heroSection} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Typography variant="h1">
                                Turbocharge your IUL with <span className={classes.leverageText}>leverage</span>
                            </Typography>
                            <Typography variant="subtitle1" className={classes.heroSubtitle}>
                                Imagine if you had someone else funding up to 70% of your IUL premiums... you would see
                                cash value growth on up to 3x as much money as you were personally paying into the
                                policy.
                            </Typography>
                            <Typography variant="subtitle1" className={classes.heroSubtitle}>
                                That's why we offer the opportunity to utilize leverage with your IUL policy.
                            </Typography>
                            <Box className={classes.heroButtons}>
                                <Button variant="outlined" color="primary" size="medium" onClick={()=>{ PCClick() }}>I don't qualify, enroll in
                                    IUL</Button>
                                <Button variant="outlined" color="primary" size="medium">Learn More</Button>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box className={classes.infoBox}>
                                <Box className={classes.moneyIconContainer}>
                                    <img src={Turbocharge} alt="Money Bag Icon"
                                         style={{ width: "120px", height: "120px" }} />
                                </Box>
                                <Typography variant="h6" className={classes.infoBoxTitle}>Qualifications</Typography>
                                <List dense>
                                    {["Your combined household income is at least $100k", "You are between the ages of 18-65", "You are in average or good health"].map(text => (
                                            <ListItem key={text} disableGutters>
                                                <ListItemIcon
                                                        className={classes.checkIcon}><CheckCircleOutlineIcon /></ListItemIcon>
                                                <ListItemText primary={text} />
                                            </ListItem>
                                    ))}
                                </List>
                                <Box mt={2} mb={2} />
                                <Typography variant="h6" className={classes.infoBoxTitle}>Advantages</Typography>
                                <List dense>
                                    {["No credit checks", "No personal guarantees", "No loan documents", "No out of pocket interest payments"].map(text => (
                                            <ListItem key={text} disableGutters>
                                                <ListItemIcon
                                                        className={classes.checkIcon}><CheckCircleOutlineIcon /></ListItemIcon>
                                                <ListItemText primary={text} />
                                            </ListItem>
                                    ))}
                                </List>
                                <Box mt={2} mb={2} />
                                <Typography variant="h6" className={classes.infoBoxTitle}>Leverage Options</Typography>
                                <List dense>
                                    {["5 year", "7 year", "10 year"].map(text => (
                                            <ListItem key={text} disableGutters>
                                                <ListItemIcon
                                                        className={classes.checkIcon}><CheckCircleOutlineIcon /></ListItemIcon>
                                                <ListItemText primary={text} />
                                            </ListItem>
                                    ))}
                                </List>
                            </Box>
                        </Grid>
                    </Grid>

                    {/* ======================= Comparison Section ======================= */}
                    <Grid container spacing={2}>
                    <Grid item xs={6} md={8}>
                        <Typography variant="h1" className="textCenter">
                            <span className={classes.leverageText}>Compare</span> strategies & potential benefits
                        </Typography>
                    </Grid>
                    </Grid>
                    <Box className={classes.comparisonSection}>
                        <Grid container spacing={2} alignItems="stretch">
                            {/* --- Calculator --- */}
                            <Grid item xs={12} md={3}>
                                <Paper className={classes.calculatorCard} elevation={3}>
                                    <Formik
                                            enableReinitialize={true}
                                            initialValues={{ ...initialValues }}
                                            validationSchema={validationSchema}
                                            onSubmit={onHandleSubmit}
                                    >
                                        {({
                                              isSubmitting,
                                              dirty,
                                              handleSubmit
                                          }) => (
                                                <>
                                                <form className="floatLeft w100" onSubmit={handleSubmit}>
                                                    <FormFields issueAge={issueAge} />
                                                    <FormikTokenUpdate getMinContribution={getMinContribution} />
                                                    <Button
                                                            className="mb20 mt20"
                                                            color="primary"
                                                            disabled={!dirty || isSubmitting}
                                                            type="submit"
                                                            variant="contained"
                                                    >
                                                        Calculate
                                                    </Button>
                                                </form>
                                                </>
                                        )}

                                    </Formik>
                                </Paper>
                            </Grid>

                            {/* --- IUL Benefits Card --- */}
                            <Grid item xs={12} md={3}>
                                <Paper className={`${classes.benefitCard} ${classes.blueCard}`} elevation={3}>
                                    <Box>
                                    <MyBenefitsPage isIul={true} responseData={iulEstimationResponse}/>
                                    </Box>
                                    <Button variant="contained" color="secondary" size="small" onClick={()=> PCClick(true)}>Enroll</Button>
                                </Paper>
                            </Grid>

                            {/* --- Leverage Benefits Card --- */}
                            <Grid item xs={12} md={3}>
                                <Paper className={`${classes.benefitCard} ${classes.greenCard}`} elevation={3}>
                                    <Box>
                                    <MyBenefitsPage isIul={false} responseData={estimationResponse }/>
                                    </Box>
                                    <Button variant="contained" color="secondary" size="small" onClick={(e)=> PCClick(false)}>Enroll</Button>
                                </Paper>
                            </Grid>

                            {/* --- More with Leverage --- */}
                            <Grid item xs={12} md={3}>
                                <Box className={classes.moreLeverageCol}>
                                    <Typography variant="h5" style={{ fontWeight: "bold", color: "#0D2F4B" }}>More with
                                        Leverage</Typography>
                                    {getDifference().map((item, index) => (
                                            <Typography key={index} variant="h5"
                                                        className={classes.leverageDiff}>{item.diff}</Typography>
                                    ))}
                                    <Button variant="contained" color="primary" size="small">Learn More</Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* ======================= CTA Section ======================= */}
                    <Grid container className={classes.ctaSection} alignItems="center">
                        <Grid item xs={12} md={4} className={classes.ctaColumn}>
                            <Typography variant="h5" className={classes.ctaTitle}>
                                Ready to <span className={classes.leverageText}>utilize leverage</span> with your IUL?
                            </Typography>
                            <Button className={`${classes.ctaButton} ${classes.ctaEnroll}`} onClick={(e) => PCClick()}>Enroll Now</Button>
                        </Grid>
                        <Grid item xs={12} md={4} className={classes.ctaColumn}>
                            <Typography variant="h5" className={classes.ctaTitle}>
                                Prefer to <span className={classes.leverageText}>learn more</span> about leverage first?
                            </Typography>
                            <Button variant="outlined" className={`${classes.ctaButton} ${classes.ctaLearn}`} onClick={KzClick}>Learn
                                More</Button>
                        </Grid>
                        <Grid item xs={12} md={4} className={classes.ctaColumn}>
                            <Typography variant="h5" className={classes.ctaTitle}>
                                Want to talk further with an <span className={classes.leverageText}>agent</span>?
                            </Typography>
                            <Button className={`${classes.ctaButton} ${classes.ctaContact}`}>Contact Agent</Button>
                        </Grid>
                    </Grid>
                </div>
            </LandingLayout>
    );
};

export default TurboChargeIul;


const FormFields = (props:any ) =>{
    const [beginDistributions, setBeginDistributions] = React.useState(setOptions(ESTIMATE_MAX_AGE));
    const [endDistributions, setEndDistributions] = React.useState<any[]>(setOptions(ESTIMATE_MAX_AGE));
    const {values, dirty, handleChange, handleBlur, errors} = useFormikContext<any>()
    const { issueAge } = props
    const userAuth = useSelector((state: RootState) => state.auth);
    const verifyInviteData = useSelector((state: RootState) => state.verify);
    const inviteToken = getQueryStringValue("token");
    const user: any = !inviteToken && userAuth ? userAuth.user : verifyInviteData;
    const client = !inviteToken && userAuth ? user.client : verifyInviteData;
    const minCon = useSelector((state: RootState) => state.minimumContribution) || 21350;
    const [ageIncrement, setAgeIncrement] = React.useState(0);

    
    console.log("==== value", values)
    const formFields = [
        {
            component: SelectField,
            grid: 12,
            label: <Label label={"Age"} required={true}/>,
            key: values.issueAge,
            name: "issueAge",
            options: ageDropDown(),
        },
        {
            component: SelectField,
            grid: 12,
            key: values.gender,
            label: <Label label={"Gender"} required={true}/>,
            name: "gender",
            options: genderDropDown,
        },
        {
            component: SelectField,
            grid: 12,
            key: values.healthType,
            label: <Label label={"Health"} required={true}/>,
            name: "healthType",
            options: healthDropdown.map((option: any) => ({...option, key: option.value})),
        },
        {
            component: SelectField,
            grid: 12,
            label: <Label label={"Tobacco/Marijuana Use"} required={true}/>,
            name: "tobacco",
            options: tobaccoDropdownOpn,
        },
        {
            component: SelectField,
            grid: 12,
            label: <Label label={"Contribution Years"} required={true}/>,
            name: "primaryFocusPlan",
            onSelectChange: (value: string) => value === IUL710PayEnum.IUL7Pay ? setAgeIncrement(IUL710TermEnum.IUL7) : setAgeIncrement(IUL710TermEnum.IUL10),
            options: primaryPlansOptions,
        },
        {
            component: SelectField,
            grid: 12,
            label: <Label label={"Premium Schedule"} required={true}/>,
            name: "contributionFrequency",
            options: contributionFrequencyOpn,
        },
        {
            component: SelectField,
            grid: 12,
            label: <Label label={"Income Start Age"} required={true}/>,
            name: "beginDistributions",
            options: beginDistributions,
        },
        {
            component: SelectField,
            grid: 12,
            label: <Label label={"Income End Age"} required={true}/>,
            name: "endDistributions",
            options: endDistributions,
        },
        {
            component: TextField,
            grid: 12,
            label: <Label label={`Enter ${values && (values as any).contributionFrequency} Contribution amount`} required={true}/>,
            name: "contributionAmount",
        },
    ];

    React.useEffect(() => {
        if (!!issueAge) {
            const beginDistVal: number = issueAge + ageIncrement;
            const res: any[] = setOptions(beginDistVal);
            setBeginDistributions(res);
            setEndDistributions(res);
        }

    }, [issueAge, ageIncrement]);

    const renderField = ({grid, ...field}: any, index: number) => (
            <Grid item xs={12} sm={grid} key={index} style={{ marginBottom: "10px" }}>
                <TextFieldCommon
                        values={values}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputLabelProps={{disabled: false}}
                        {...field}
                />
                <ErrorHelper errors={errors} name={field.name}/>
            </Grid>
    );

    return <>
        {
            formFields.map(renderField)
        }
    </>
}